import { API_URL } from '../config';
import { safeFetch } from '../utils/safeFetch';
import { uploadProtectedFileWithProgress } from '../utils/protectedMedia';

const getHeaders = (extraHeaders = {}) => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  ...extraHeaders,
});

const buildQuery = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, value);
    }
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

export const getTasks = (params = {}) => (
  safeFetch(`${API_URL}/api/tasks${buildQuery(params)}`, { headers: getHeaders() })
);

export const getTask = (taskId) => (
  safeFetch(`${API_URL}/api/tasks/${taskId}`, { headers: getHeaders() })
);

export const createTask = (payload) => (
  safeFetch(`${API_URL}/api/tasks`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  })
);

export const updateTask = (taskId, payload) => (
  safeFetch(`${API_URL}/api/tasks/${taskId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  })
);

export const takeTask = (taskId) => (
  safeFetch(`${API_URL}/api/tasks/${taskId}/take`, {
    method: 'POST',
    headers: getHeaders(),
  })
);

export const assignTask = (taskId, userId) => (
  safeFetch(`${API_URL}/api/tasks/${taskId}/assign`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ user_id: userId }),
  })
);

export const completeTask = (taskId, forceWithoutPhoto = false, options = {}) => (
  safeFetch(`${API_URL}/api/tasks/${taskId}/complete`, {
    method: 'POST',
    headers: getHeaders(options.dispatcherAccess ? { 'X-Dispatcher-Access': '1' } : {}),
    body: JSON.stringify({ force_without_photo: forceWithoutPhoto }),
  })
);

export const cancelTask = (taskId, reason = '') => (
  safeFetch(`${API_URL}/api/tasks/${taskId}/cancel`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ reason }),
  })
);

export const deferTask = (taskId, payload, options = {}) => (
  safeFetch(`${API_URL}/api/tasks/${taskId}/defer`, {
    method: 'POST',
    headers: getHeaders(options.dispatcherAccess ? { 'X-Dispatcher-Access': '1' } : {}),
    body: JSON.stringify(payload),
  })
);

export const resolveDeferredTask = (taskId, payload) => (
  safeFetch(`${API_URL}/api/tasks/${taskId}/deferred/resolve`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  })
);

export const getTaskComments = (taskId) => (
  safeFetch(`${API_URL}/api/tasks/${taskId}/comments`, { headers: getHeaders() })
);

export const addTaskComment = (taskId, text) => (
  safeFetch(`${API_URL}/api/tasks/${taskId}/comments`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ text }),
  })
);

export const markTaskCommentRead = (taskId, commentId) => (
  safeFetch(`${API_URL}/api/tasks/${taskId}/comments/${commentId}/read`, {
    method: 'POST',
    headers: getHeaders(),
  })
);

export const getTaskPhotos = (taskId) => (
  safeFetch(`${API_URL}/api/tasks/${taskId}/photos`, { headers: getHeaders() })
);

export const getTaskHistory = (taskId, limit = 200) => (
  safeFetch(`${API_URL}/api/tasks/${taskId}/history${buildQuery({ limit })}`, { headers: getHeaders() })
);

export const getTaskAssignees = () => (
  safeFetch(`${API_URL}/api/tasks/assignees`, { headers: getHeaders() })
);

export const uploadTaskPhoto = async (taskId, file, caption = '', onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  if (caption) {
    formData.append('caption', caption);
  }

  return uploadProtectedFileWithProgress({
    url: `${API_URL}/api/tasks/${taskId}/photos`,
    formData,
    onProgress,
  });
};

export const deleteTaskPhoto = (taskId, photoId) => (
  safeFetch(`${API_URL}/api/tasks/${taskId}/photos/${photoId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  })
);
